import { ArrowLeft, Building2, Check, Loader2, User } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useIsMobile } from 'hooks'
import { MobilePageHeader } from '../../creative/components/MobilePageHeader'
import {
  LinkedInTargetsApi,
  type LinkedInTargetSummary,
  type LinkedInTargetsResponse,
} from '../api/linkedin-targets-api'
import { finishOAuthPickerStep } from '../utils/oauth-popup-complete'

type SelectedKey = `${'person' | 'organization'}:${string}`

function keyOf(type: 'person' | 'organization', id: string): SelectedKey {
  return `${type}:${id}` as SelectedKey
}

export function LinkedInTargetPickerPage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [params] = useSearchParams()
  const fromOAuth = params.get('fromOAuth') === '1'

  const [data, setData] = useState<LinkedInTargetsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedKey, setSelectedKey] = useState<SelectedKey | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    LinkedInTargetsApi.list()
      .then(res => {
        if (cancelled) return
        setData(res)
        // Pre-select only a target that is actually one of the shown options.
        // Never default to personal: arriving from the Company Page flow there
        // may be no bound target yet, and silently preferring personal is how an
        // org connect ends up publishing to the wrong account. We also guard the
        // bound target against a stale `selected` that's no longer offered (e.g.
        // a person target after the personal token was disconnected). A single
        // option is pre-selected only when it's safe to (see `onlyOption` below);
        // otherwise we force an explicit choice.
        const availableKeys = new Set<SelectedKey>([
          ...(res.personal ? [keyOf('person', res.personal.id)] : []),
          ...res.organizations.map(o => keyOf('organization', o.id)),
        ])
        const boundKey = res.selected ? keyOf(res.selected.type, res.selected.id) : null
        const lone: SelectedKey | null = availableKeys.size === 1 ? [...availableKeys][0] : null
        // During the Company-Page OAuth redirect (`fromOAuth`), never auto-select a
        // lone `person` option. If the org list transiently failed to load,
        // personal becomes the sole option and silently picking it is exactly how a
        // Company-Page connect ends up bound to the personal profile. Force an
        // explicit choice instead. Outside the OAuth flow (manual "Change") a single
        // option is safe to pre-select.
        const onlyOption: SelectedKey | null =
          lone && fromOAuth && lone.startsWith('person:') ? null : lone

        if (boundKey && availableKeys.has(boundKey)) {
          setSelectedKey(boundKey)
        } else if (onlyOption) {
          setSelectedKey(onlyOption)
        }
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load LinkedIn targets:', err)
        setError('We could not load your LinkedIn targets. Please try again.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [fromOAuth])

  const options = useMemo(() => {
    if (!data)
      return [] as Array<{
        key: SelectedKey
        type: 'person' | 'organization'
        target: LinkedInTargetSummary
      }>
    const all: Array<{
      key: SelectedKey
      type: 'person' | 'organization'
      target: LinkedInTargetSummary
    }> = []
    if (data.personal) {
      all.push({ key: keyOf('person', data.personal.id), type: 'person', target: data.personal })
    }
    for (const org of data.organizations) {
      all.push({ key: keyOf('organization', org.id), type: 'organization', target: org })
    }
    return all
  }, [data])

  // The confirm CTA only earns the acid treatment once an actual target is
  // picked. Without this, a `null` (Company-Page pick pending) state left the
  // button disabled-but-still-acid (`disabled:opacity-50`), which reads as an
  // active CTA even though nothing is selected. Muted style until a real choice.
  const canConfirm = !!selectedKey && options.length > 0

  const handleConfirm = useCallback(async () => {
    if (!selectedKey || !data) return
    const picked = options.find(o => o.key === selectedKey)
    if (!picked) return

    setSaving(true)
    try {
      await LinkedInTargetsApi.select({ type: picked.type, id: picked.target.id })
      // Personal + Company Page share ONE `linkedin` card/row now, and the server
      // emits its post-select Pusher as `linkedin` — so the opener handshake +
      // card refresh key off `linkedin` (matches select-target's OAuthUpdated).
      finishOAuthPickerStep({ provider: 'linkedin', fromOAuth, navigate })
    } catch (err) {
      console.error('Failed to save LinkedIn target:', err)
      setError('We could not save your selection. Please try again.')
    } finally {
      setSaving(false)
    }
  }, [data, fromOAuth, navigate, options, selectedKey])

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-paper-faint" />
      </div>
    )
  }

  return (
    <div className={isMobile ? 'flex h-full flex-col' : 'mx-auto max-w-2xl px-6 py-6'}>
      {isMobile ? (
        <MobilePageHeader
          title="Choose your LinkedIn target"
          subtitle={
            fromOAuth
              ? 'You connected LinkedIn. Pick where you want to publish.'
              : 'Pick where Taka should publish on your behalf.'
          }
        />
      ) : (
        <div className="mb-6 flex items-start gap-3">
          {!fromOAuth && (
            <button
              type="button"
              onClick={() => navigate('/integrations')}
              className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-[8px] border border-line-bright text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper"
              aria-label="Back to integrations"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <div>
            <h1 className="text-xl font-semibold text-paper">Choose your LinkedIn target</h1>
            <p className="mt-1 text-sm text-paper-dim">
              {fromOAuth
                ? 'You connected LinkedIn. Pick where you want Taka to publish.'
                : 'Pick where Taka should publish on your behalf. You can change this anytime.'}
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="alert mb-4" data-tone="error" role="alert">
          {error}
        </div>
      )}

      <div className={`space-y-3 ${isMobile ? 'px-4 py-4' : ''}`}>
        {/* The error alert above already explains a failed load — stacking the
            "no targets" empty box under it read as two contradictory states. */}
        {error ? null : options.length === 0 ? (
          <div className="rounded-xl border border-line bg-bg-2 p-4 text-[13px] text-paper-dim">
            We could not find any LinkedIn targets for your account.
          </div>
        ) : (
          options.map(({ key, type, target }) => {
            const isSelected = selectedKey === key
            return (
              <button
                key={key}
                type="button"
                data-active={isSelected}
                aria-pressed={isSelected}
                onClick={() => setSelectedKey(key)}
                className="select-card flex w-full items-center gap-4 rounded-xl p-4 text-left transition-shadow hover:shadow-sm"
              >
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-line bg-bg-3">
                  {target.avatarUrl ? (
                    <img
                      src={target.avatarUrl}
                      alt={target.name}
                      className="h-10 w-10 object-cover"
                    />
                  ) : type === 'person' ? (
                    <User size={20} className="text-paper-faint" />
                  ) : (
                    <Building2 size={20} className="text-paper-faint" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-paper">{target.name}</span>
                  {target.vanityName && (
                    <p className="text-sm text-paper-dim">
                      linkedin.com/company/{target.vanityName}
                    </p>
                  )}
                </div>

                <div className="flex-shrink-0">
                  <div
                    className={`flex h-6 w-6 items-center justify-center rounded-full border ${
                      isSelected
                        ? 'text-ink-on-acid border-acid bg-acid'
                        : 'border-line-bright bg-bg-3'
                    }`}
                  >
                    {isSelected && <Check size={14} />}
                  </div>
                </div>
              </button>
            )
          })
        )}

        <div className="flex justify-end gap-2 pt-2">
          {!fromOAuth && (
            <button
              type="button"
              onClick={() => navigate('/integrations')}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-[8px] border border-line-bright px-4 py-2 text-[13px] text-paper transition-colors hover:bg-bg-3 disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm || saving}
            className={`inline-flex items-center gap-1.5 rounded-[8px] px-4 py-2 text-[13px] font-semibold transition-all ${
              canConfirm
                ? 'text-ink-on-acid bg-acid shadow-[var(--shadow-acid-cta)] hover:-translate-y-px disabled:opacity-50'
                : 'cursor-not-allowed bg-bg-3 text-paper-faint'
            }`}
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {saving ? 'Saving…' : fromOAuth ? 'Continue' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
